/**
 * @function eventListenerGoodData
 *
 * Call this function to start an event listener for GoodData events.  All possible events are outlined below.
 * Will call the callback functiuon you provide and give you the event type and object.
 *
 * @param {(type: string, event: object) => void} callback - A function to provide you with event context when a GoodData event is heard.
 *
 * @return {void}
 *
 * @example
 *
 *     eventListenerGoodData((type: string, event: any) => {
 *        switch (type) {
 *          // change functionality based on type here
 *        }
 *     })
 */
export const eventListenerGoodData = (
  callback: (type: string, event: any) => void
) => {
  window.addEventListener("message", event => {
    if (typeof event.data != "string") return;
    const message = JSON.parse(event.data);
    if (message.gdc) {
      switch (message.gdc.type) {
        case "app.ok":
          switch (message.gdc.name) {
            case "platform.info":
              // GoodData is initialized, loaded correctly.
              callback("platform.info", message);
              break;
            case "report.info":
              // A report has loaded correctly.
              callback("report.info", message);
              break;
            case "project.info.no.permission":
              // A user tries to access an embedded dashboard in a project that they do not have access rights to.
              callback("project.info.no.permission", message);
              break;
            case "embedded.init":
              // The content of the embedded dashboard starts loading after a user chose to export to PDF the dashboard that hosts the embedded dashboard.
              callback("embedded.init", message);
              break;
            case "embedded.ready":
              // The content of the embedded dashboard completes loading.
              callback("embedded.ready", message);
              break;
            case "ui.frameinfo":
              // The key "message.gdc.name.data" will contain the height and width of the iframe.
              callback("ui.frameinfo", message);
              break;
            case "filter.value.changed":
              // One of the filters on the page has changed values.
              // The key "message.gdc.name.data" will contain the exact filter and it's new value.
              callback("filter.value.changed", message);
              break;
            case "ui.active":
              // The user is actively working within the iframe, scrolling, clicking, etc.
              callback("ui.active", message);
              break;
            case "sst.expired":
              // The SuperSecure Token (SST) has expired.
              callback("sst.expired", message);
              break;
          }
          break;

        case "app.down":
          switch (message.gdc.name) {
            case "platform.maintenance":
              // Application servers are locked for maintenance.
              callback("platform.maintenance", message);
              break;
            case "platform.upgrade":
              // Application servers are locked for upgrading to a new version.
              callback("platform.upgrade", message);
              break;
            case "project.maintenance":
              // The project is under maintenance.
              callback("project.maintenance", message);
              break;
            case "platform.error":
              // A general error occurs.
              callback("platform.error", message);
              break;
          }
          break;

        case "app.event":
          switch (message.gdc.name) {
            case "ui.drill":
              // User Drilled into a Graph.
              // The key "message.gdc.name.data" will contain the all data pertaining to this event,
              // including relevant object and report uris.
              callback("ui.drill", message);
              break;
            case "ui.link":
              // User clicked a link to somewhere else, navigating away from the dashboard.
              // The key "message.gdc.name.data" will contain the all data pertaining to this event,
              // including a uri so that you can redirect.
              callback("ui.link", message);
              break;
            case "report.export":
              // A user clicks the button to export a report from the dashboard.
              // The key "message.gdc.name.data" will contain the all data pertaining to this event,
              // including a report uri.
              callback("report.export", message);
              break;
          }
          break;
      }
    }
  });
};
try {
    // @ts-ignore
    eventListenerGoodData.displayName = "eventListenerGoodData";
    // @ts-ignore
    eventListenerGoodData.__docgenInfo = { "description": "", "displayName": "eventListenerGoodData", "props": { "apply": { "defaultValue": null, "description": "Calls the function, substituting the specified object for the this value of the function, and the specified array for the arguments of the function.\n@param thisArg The object to be used as the this object.\n@param argArray A set of arguments to be passed to the function.", "name": "apply", "required": true, "type": { "name": "(this: Function, thisArg: any, argArray?: any) => any" } }, "call": { "defaultValue": null, "description": "Calls a method of an object, substituting another object for the current object.\n@param thisArg The object to be used as the current object.\n@param argArray A list of arguments to be passed to the method.", "name": "call", "required": true, "type": { "name": "(this: Function, thisArg: any, ...argArray: any[]) => any" } }, "bind": { "defaultValue": null, "description": "For a given function, creates a bound function that has the same body as the original function.\nThe this object of the bound function is associated with the specified object, and has the specified initial parameters.\n@param thisArg An object to which the this keyword can refer inside the new function.\n@param argArray A list of arguments to be passed to the new function.", "name": "bind", "required": true, "type": { "name": "(this: Function, thisArg: any, ...argArray: any[]) => any" } }, "toString": { "defaultValue": null, "description": "Returns a string representation of a function.", "name": "toString", "required": false, "type": { "name": "() => string" } }, "prototype": { "defaultValue": null, "description": "", "name": "prototype", "required": true, "type": { "name": "any" } }, "length": { "defaultValue": null, "description": "", "name": "length", "required": true, "type": { "name": "number" } }, "arguments": { "defaultValue": null, "description": "", "name": "arguments", "required": true, "type": { "name": "any" } }, "caller": { "defaultValue": null, "description": "", "name": "caller", "required": true, "type": { "name": "Function" } }, "name": { "defaultValue": null, "description": "Returns the name of the function. Function names are read-only and can not be changed.", "name": "name", "required": true, "type": { "name": "string" } }, "__@hasInstance": { "defaultValue": null, "description": "Determines whether the given value inherits from this function if this function was used\nas a constructor function.\n\nA constructor function can control which objects are recognized as its instances by\n'instanceof' by overriding this method.", "name": "__@hasInstance", "required": true, "type": { "name": "(value: any) => boolean" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/shared/containers/GoodData/utilities/eventListenerGoodData.ts#eventListenerGoodData"] = { docgenInfo: eventListenerGoodData.__docgenInfo, name: "eventListenerGoodData", path: "src/shared/containers/GoodData/utilities/eventListenerGoodData.ts#eventListenerGoodData" };
}
catch (__react_docgen_typescript_loader_error) { }